import React, { useRef } from 'react';

import { Alert, Checkbox, CircularProgress, Dialog, FormControlLabel, FormHelperText, MenuItem } from '@mui/material';
import { Dayjs } from 'dayjs';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { Button } from 'components/Button';
import { StyledDatePicker, StyledSelect, StyledTextField } from 'components/Input/styles';
import { colors } from 'theme/colors';
import type { CreateTrainingRecordPayload } from 'utils/api/modules/trainingRecords/trainingRecords.types';

import { STATUS_OPTIONS } from './config';
import { DialogActions, FileUploadWrapper, StyledDialogContent, StyledDialogTitle } from './styles';

interface AddRecordModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateTrainingRecordPayload) => Promise<void>;
  contactEmail: string;
}

interface FormValues {
  moduleName: string;
  isExempt: boolean;
  courseDate: Dayjs | null;
  expiryDate: Dayjs | null;
  statusCode: number | '';
  certFile: File | null;
}

const initialValues: FormValues = {
  moduleName: '',
  isExempt: false,
  courseDate: null,
  expiryDate: null,
  statusCode: '',
  certFile: null,
};

const validationSchema = Yup.object({
  moduleName: Yup.string().trim().required('Please enter a module name.'),
  isExempt: Yup.boolean().required(),
  courseDate: Yup.mixed()
    .nullable()
    .test('course-date-required', 'Please select a course date.', function courseDateRequired(value) {
      return this.parent.isExempt || Boolean(value);
    }),
  expiryDate: Yup.mixed().nullable(),
  statusCode: Yup.mixed(),
  certFile: Yup.mixed()
    .nullable()
    .test('pdf-type', 'Only PDF files are accepted. Please upload a valid PDF.', (value) => {
      if (!value) return true;
      const file = value as File;
      return file.type === 'application/pdf' && file.name.toLowerCase().endsWith('.pdf');
    }),
});

const AddRecordModal: React.FC<AddRecordModalProps> = ({ open, onClose, onSubmit, contactEmail }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const titleId = React.useId();

  const handleFormSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>): Promise<void> => {
    try {
      const selectedStatus = STATUS_OPTIONS.find((o) => o.code === values.statusCode);
      const payload: CreateTrainingRecordPayload = {
        contactEmail,
        moduleName: values.moduleName.trim(),
      };

      if (values.isExempt) {
        payload.isExempt = true;
      } else {
        if (values.courseDate) payload.courseDate = values.courseDate.format('YYYY-MM-DD');
        if (values.expiryDate) payload.expiryDate = values.expiryDate.format('YYYY-MM-DD');
        if (selectedStatus) {
          payload.status = selectedStatus.label;
          if (selectedStatus.passFail !== null) {
            payload.passFail = selectedStatus.passFail;
          }
        }
        if (values.certFile) {
          await new Promise<void>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              payload.certificateFile = result.replace(/^data:[^;]+;base64,/, '');
              payload.certificateFilename = (values.certFile as File).name;
              resolve();
            };
            reader.onerror = reject;
            reader.readAsDataURL(values.certFile as File);
          });
        }
      }

      await onSubmit(payload);
      helpers.resetForm();
      if (fileInputRef.current) fileInputRef.current.value = '';
      onClose();
    } catch {
      helpers.setStatus('Failed to save training record. Please try again.');
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleFormSubmit}>
      {({ values, errors, touched, isSubmitting, setFieldValue, setFieldTouched, status, resetForm, submitCount }) => {
        const handleClose = (): void => {
          resetForm();
          if (fileInputRef.current) fileInputRef.current.value = '';
          onClose();
        };

        const showError = (field: keyof FormValues): boolean =>
          (touched[field] || submitCount > 0) && Boolean(errors[field]);

        return (
          <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth aria-labelledby={titleId}>
            <Form>
              <StyledDialogTitle id={titleId}>Add Training Record</StyledDialogTitle>
              <StyledDialogContent>
                {status && <Alert severity="error">{status}</Alert>}
                <StyledTextField
                  placeholder="Module Name"
                  value={values.moduleName}
                  onChange={(e) => setFieldValue('moduleName', e.target.value)}
                  onBlur={() => setFieldTouched('moduleName', true)}
                  error={showError('moduleName')}
                  helperText={showError('moduleName') ? errors.moduleName : undefined}
                  fullWidth
                  required
                />
                <FormControlLabel
                  control={
                    <Checkbox checked={values.isExempt} onChange={(e) => setFieldValue('isExempt', e.target.checked)} />
                  }
                  label="Mark as exempt"
                  sx={{ alignSelf: 'flex-start' }}
                />
                {!values.isExempt && (
                  <>
                    <StyledDatePicker
                      label="Course Date *"
                      format="DD/MM/YYYY"
                      value={values.courseDate}
                      onChange={(val) => setFieldValue('courseDate', val)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: showError('courseDate'),
                          helperText: showError('courseDate') ? (errors.courseDate as string) : undefined,
                          onBlur: () => setFieldTouched('courseDate', true),
                        },
                      }}
                    />
                    <StyledDatePicker
                      label="Expiry Date"
                      format="DD/MM/YYYY"
                      value={values.expiryDate}
                      onChange={(val) => setFieldValue('expiryDate', val)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                    <StyledSelect
                      value={values.statusCode}
                      onChange={(e) => setFieldValue('statusCode', e.target.value as number)}
                      displayEmpty
                      fullWidth
                      renderValue={(val) =>
                        val === '' ? (
                          <span style={{ color: colors.text.secondary }}>Status</span>
                        ) : (
                          (STATUS_OPTIONS.find((o) => o.code === val)?.label ?? '')
                        )
                      }
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.code} value={opt.code}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                    <FileUploadWrapper>
                      <Button variant="outlined" size="medium" onClick={() => fileInputRef.current?.click()}>
                        {values.certFile ? values.certFile.name : 'Upload Certificate (PDF)'}
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0] ?? null;
                          setFieldValue('certFile', file);
                          setFieldTouched('certFile', true);
                          if (file && (file.type !== 'application/pdf' || !file.name.toLowerCase().endsWith('.pdf'))) {
                            e.target.value = '';
                          }
                        }}
                      />
                      {showError('certFile') && <FormHelperText error>{errors.certFile as string}</FormHelperText>}
                    </FileUploadWrapper>
                  </>
                )}
              </StyledDialogContent>
              <DialogActions>
                <Button variant="outlined" size="large" onClick={handleClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button variant="contained" size="large" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? <CircularProgress size={20} /> : 'Create'}
                </Button>
              </DialogActions>
            </Form>
          </Dialog>
        );
      }}
    </Formik>
  );
};

export default AddRecordModal;
